import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  SyntheticEvent,
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DropdownMenuItem } from "../components/types";
import {
  CampaignPosition,
  CampaignVisualSettingsDto,
} from "../shared/types/campaignTypes";
import { BorderType } from "../components/NotificationEditComponents/BorderSelect";
import { SelectChangeEvent } from "@mui/material";
import {
  convertStringToLanguage,
  transformFormsToUrlFilterDto,
  transformUrlFilterToForms,
} from "../generic-utils";
import { CampaignManager } from "../services/api/CampaignManager";
import { showFlashMessageWithTimeout } from "../services/actions/flashMessage-actions";
import DOMPurify from "dompurify";
import { useEditCampaign } from "./EditCampaignContext";
import { useParams } from "react-router-dom";
import { NotificationDefaultSettings } from "../types/notification-default-settings";
import { getCustomWebLogo } from "../services/selectors/web-selectors";

type ControlType = "beginsWith" | "contains" | "isEqual";
type TabType = "settings" | "view" | "notifications" | "analytics" | "branding";

interface PcSettingsType {
  isActive: boolean;
  position: CampaignPosition;
  showAfterSeconds?: number;
  showDurationSeconds?: number;
  nextNotificationDelaySeconds?: number;
  animationSpeed?: number;
}

interface PhoneSettingsType {
  isActive: boolean;
  position: CampaignPosition;
  showAfterSeconds?: number;
  showDurationSeconds?: number;
  nextNotificationDelaySeconds?: number;
  animationSpeed?: number;
}

interface FormType {
  control: ControlType;
  include: string[];
  excludes: string[];
}

interface CampaignContextType {
  alignment: "all" | "custom";
  forms: FormType[];
  pcPreview: string;
  phonePreview: string;
  pcSettings: PcSettingsType;
  phoneSettings: PhoneSettingsType;
  menuItems: DropdownMenuItem[];
  campaignName: string;
  selectedTab: TabType;
  selectedBorder: BorderType | null;
  editCampaignName: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isDefaultUpdateLoading: boolean;
  isModalOpen: boolean;
  showIcon: boolean;
  textColor?: string;
  backgroundColor?: string;
  borderRadius?: number;
  backgroundOpacity?: number;
  iconColor?: string;
  iconBackgroundColor?: string;
  iconBackgroundRadius?: number;
  modalOpen: boolean;
  activeSize: "classic" | "small" | "compact";
  createPcPreview: () => Promise<void>;
  createPhonePreview: () => Promise<void>;
  setActiveSize: Dispatch<SetStateAction<"classic" | "small" | "compact">>;
  setTextColor: Dispatch<SetStateAction<string | undefined>>;
  setIconColor: Dispatch<SetStateAction<string | undefined>>;
  setShowIcon: Dispatch<SetStateAction<boolean>>;
  setBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  setIconBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  setBorderRadius: Dispatch<SetStateAction<number | undefined>>;
  setIconBackgroundRadius: Dispatch<SetStateAction<number | undefined>>;
  setBackgroundOpacity: Dispatch<SetStateAction<number | undefined>>;
  setPcSettings: Dispatch<SetStateAction<PcSettingsType>>;
  setPhoneSettings: Dispatch<SetStateAction<PhoneSettingsType>>;
  setIsDefaultUpdateLoading: Dispatch<SetStateAction<boolean>>;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  setPcPreview: Dispatch<SetStateAction<string>>;
  setPhonePreview: Dispatch<SetStateAction<string>>;
  handleChange: (event: SyntheticEvent, newValue: string) => void;
  handleEditCampaignName: () => void;
  handleFormControlChange: (event: SelectChangeEvent, index: number) => void;
  handleAlignmentChange: (event: any, newAlignment: "all" | "custom") => void;
  handleNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  getValuesToHide: (forms: FormType[]) => string[];
  getLocalSettings: (
    settings: CampaignVisualSettingsDto,
    type: "pc" | "phone",
  ) => PcSettingsType | PhoneSettingsType;
  onDeleteForm: (index: number) => void;
  onAddFormIncludeChip: (formIndex: number, values: string[]) => void;
  onAddExcludeChip: (formIndex: number, values: string[]) => void;
  onSaveChanges: () => void;
  onCampaignNameChanges: (newName: string) => void;
  onCampaignNameSave: (newName: string) => void;
  addEmptyForm: () => void;
}

const CampaignContext = createContext<CampaignContextType | undefined>(
  undefined,
);

interface CampaignProviderProps {
  children: React.ReactNode;
}

export const CampaignProvider: React.FC<CampaignProviderProps> = ({
  children,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const language = convertStringToLanguage(i18n.language);
  const _campaignManager = CampaignManager.getManager();
  const { reset, save, setDetectedChange, setReset, setSave } =
    useEditCampaign();

  const customWebBranding = useSelector(getCustomWebLogo);

  const [campaignName, setCampaignName] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState<TabType>("view");
  const [forms, setForms] = useState<FormType[]>([
    { control: "contains", include: [], excludes: [] },
  ]);
  const [editCampaignName, setEditCampaignName] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [alignment, setAlignment] = useState<"all" | "custom">("all");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDefaultUpdateLoading, setIsDefaultUpdateLoading] =
    useState<boolean>(false);
  const [pcPreview, setPcPreview] = useState<string>("");
  const [phonePreview, setPhonePreview] = useState<string>("");
  const [selectedBorder, setSelectedBorder] = useState<BorderType | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [isBrandingLoaded, setIsBrandingLoaded] = useState(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [showIconDraft, setShowIconDraft] = useState<boolean>(true);
  const [textColor, setTextColor] = useState<string | undefined>(
    NotificationDefaultSettings.TextColor,
  );
  const [textColorDraft, setTextColorDraft] = useState<string | undefined>(
    NotificationDefaultSettings.TextColor,
  );
  const [backgroundColor, setBackgroundColor] = useState<string>();
  const [backgroundColorDraft, setBackgroundColorDraft] = useState<string>();
  const [borderRadius, setBorderRadius] = useState<number>();
  const [borderRadiusDraft, setBorderRadiusDraft] = useState<number>();
  const [backgroundOpacity, setBackgroundOpacity] = useState<number>();
  const [backgroundOpacityDraft, setBackgroundOpacityDraft] =
    useState<number>();
  const [iconColor, setIconColor] = useState<string>();
  const [iconColorDraft, setIconColorDraft] = useState<string>();
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string>();
  const [iconBackgroundColorDraft, setIconBackgroundColorDraft] =
    useState<string>();
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<number>();
  const [iconBackgroundRadiusDraft, setIconBackgroundRadiusDraft] =
    useState<number>();
  const [activeSize, setActiveSize] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [activeSizeDraft, setActiveSizeDraft] = useState<
    "classic" | "small" | "compact"
  >("classic");

  const [pcSettings, setPcSettings] = useState<PcSettingsType>({
    isActive: false,
    position: "bottomLeft",
    showAfterSeconds: 5,
    showDurationSeconds: 5,
    nextNotificationDelaySeconds: 10,
    animationSpeed: 50,
  });

  const [phoneSettings, setPhoneSettings] = useState<PhoneSettingsType>({
    isActive: false,
    position: "bottom",
    showAfterSeconds: 5,
    showDurationSeconds: 5,
    nextNotificationDelaySeconds: 10,
    animationSpeed: 50,
  });

  const [campaignNameDraft, setCampaignNameDraft] = useState<string>("");
  const [alignmentDraft, setAlignmentDraft] = useState<"all" | "custom">("all");
  const [formsDraft, setFormsDraft] = useState<FormType[]>([
    { control: "contains", include: [], excludes: [] },
  ]);

  const [selectedBorderDraft, setSelectedBorderDraft] =
    useState<BorderType | null>(null);

  const [pcSettingsDraft, setPcSettingsDraft] = useState<PcSettingsType>({
    isActive: false,
    position: "bottomLeft",
    showAfterSeconds: 5,
    showDurationSeconds: 5,
    nextNotificationDelaySeconds: 10,
    animationSpeed: 50,
  });

  const [phoneSettingsDraft, setPhoneSettingsDraft] =
    useState<PhoneSettingsType>({
      isActive: false,
      position: "bottom",
      showAfterSeconds: 5,
      showDurationSeconds: 5,
      nextNotificationDelaySeconds: 10,
      animationSpeed: 50,
    });

  const getLocalSettings = (
    settings: CampaignVisualSettingsDto,
    type: "pc" | "phone",
  ) => {
    if (type === "pc") {
      let result: PcSettingsType = {
        isActive: settings.isActive !== undefined ? settings.isActive : true,
        position: settings.position ? settings.position : "bottomLeft",
        showAfterSeconds: settings.showAfterSeconds
          ? settings.showAfterSeconds
          : 5,
        showDurationSeconds: settings.showDurationSeconds
          ? settings.showDurationSeconds
          : 5,
        nextNotificationDelaySeconds: settings.nextNotificationDelaySeconds
          ? settings.nextNotificationDelaySeconds
          : 10,
        animationSpeed: settings.animationSpeed ? settings.animationSpeed : 0,
      };
      return result;
    } else {
      let result: PhoneSettingsType = {
        isActive: settings.isActive !== undefined ? settings.isActive : true,
        position: settings.position ? settings.position : "bottom",
        showAfterSeconds: settings.showAfterSeconds
          ? settings.showAfterSeconds
          : 5,
        showDurationSeconds: settings.showDurationSeconds
          ? settings.showDurationSeconds
          : 5,
        nextNotificationDelaySeconds: settings.nextNotificationDelaySeconds
          ? settings.nextNotificationDelaySeconds
          : 10,
        animationSpeed: settings.animationSpeed ? settings.animationSpeed : 0,
      };
      return result;
    }
  };

  const createPcPreview = async () => {
    setPcPreview("");
    const result = await window.getNotificationPreview(
      {
        campaign: {
          branding: {
            textColor: textColor,
            backgroundColor: backgroundColor,
            borderRadius: borderRadius,
            backgroundOpacity: backgroundOpacity,
            iconColor: iconColor,
            iconBackgroundColor: iconBackgroundColor,
            iconBackgroundRadius: iconBackgroundRadius,
            activeSize: activeSize,
            brandingIsShowingIcon: showIcon,
            logoDisplay: customWebBranding?.logoDisplay,
            logo: customWebBranding?.logo,
            logoBackgroundColor: customWebBranding?.logoBackgroundColor,
            logoText: customWebBranding?.logoText,
          },
          notificationVisualSettings: {
            roundness: 0,
          },
        },
        settings: {
          notificationVisualSettings: {
            backgroundColor: backgroundColor ?? "",
            textColor: textColor ?? "",
            iconBackgroundColor: iconBackgroundColor ?? "",
            iconColor: iconColor ?? "",
            icon: "",
            roundness: borderRadius ?? 0,
            linkTextColor: "",
            linkButtonBackgroundColor: "",
            backgroundOpacity: backgroundOpacity ?? 1,
            activeSize: activeSize ?? "classic",
            isShowingIcon: showIcon ?? true,
            iconBackgroundRadius: iconBackgroundRadius ?? 0,
          },
        },
      },
      "lastOrders",
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      t("campaign.notifications.lastOrders.previewText2") ?? "",
      undefined,
      language,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      pcSettings.animationSpeed,
    );
    const cleanHtmlString = DOMPurify.sanitize(result, {
      USE_PROFILES: { html: true, svg: true },
    });
    setPcPreview(cleanHtmlString);
  };

  const createPhonePreview = async () => {
    setPhonePreview("");
    const result = await window.getNotificationPreview(
      {
        campaign: {
          branding: {
            textColor: textColor,
            backgroundColor: backgroundColor,
            borderRadius: borderRadius,
            backgroundOpacity: backgroundOpacity,
            iconColor: iconColor,
            iconBackgroundColor: iconBackgroundColor,
            iconBackgroundRadius: iconBackgroundRadius,
            activeSize: activeSize,
            brandingIsShowingIcon: showIcon,
            logoDisplay: customWebBranding?.logoDisplay,
            logo: customWebBranding?.logo,
            logoBackgroundColor: customWebBranding?.logoBackgroundColor,
            logoText: customWebBranding?.logoText,
          },
          notificationVisualSettings: {
            roundness: 0,
          },
        },
        settings: {
          notificationVisualSettings: {
            backgroundColor: backgroundColor ?? "",
            textColor: textColor ?? "",
            iconBackgroundColor: iconBackgroundColor ?? "",
            iconColor: iconColor ?? "",
            icon: "",
            roundness: borderRadius ?? 0,
            linkTextColor: "",
            linkButtonBackgroundColor: "",
            backgroundOpacity: backgroundOpacity ?? 1,
            activeSize: activeSize ?? "classic",
            isShowingIcon: showIcon ?? true,
            iconBackgroundRadius: iconBackgroundRadius ?? 0,
          },
        },
      },
      "lastOrders",
      t("campaign.notifications.lastOrders.previewText1") ?? "",
      t("campaign.notifications.lastOrders.previewText2") ?? "",
      undefined,
      language,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      phoneSettings.animationSpeed,
    );
    const cleanHtmlString = DOMPurify.sanitize(result, {
      USE_PROFILES: { html: true, svg: true },
    });

    setPhonePreview(cleanHtmlString);
  };

  useEffect(() => {
    if (showIcon) {
      setIconBackgroundColor(iconBackgroundColorDraft);
      setIconColor(iconColorDraft);
      setIconBackgroundRadius(iconBackgroundRadiusDraft);
    }
  }, [showIcon]);

  useLayoutEffect(() => {
    createPhonePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneSettings.animationSpeed]);

  useLayoutEffect(() => {
    createPcPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcSettings.animationSpeed]);

  useLayoutEffect(() => {
    createPcPreview();
    createPhonePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    textColor,
    backgroundColor,
    borderRadius,
    backgroundOpacity,
    iconColor,
    iconBackgroundColor,
    iconBackgroundRadius,
    activeSize,
    showIcon,
    customWebBranding,
  ]);

  useEffect(() => {
    if (id) {
      const fetchCampaign = async () => {
        try {
          setIsFetching(true);
          const result = await _campaignManager.getSingleCampaignAsync(id);
          const transformedForms = transformUrlFilterToForms(result.urlFilter);
          if (transformedForms.length > 0) {
            setAlignment("custom");
            setAlignmentDraft("custom");
            setForms(transformedForms);
            setFormsDraft(transformedForms);
          }

          setCampaignName(result.name);
          setCampaignNameDraft(result.name);
          setIsFetching(false);
        } catch (e: any) {
          setIsFetching(false);
          showFlashMessageWithTimeout(dispatch, e.toString(), "error");
        }
      };

      fetchCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      const fetchCampaign = async () => {
        try {
          setIsFetching(true);
          const _campaignManager = CampaignManager.getManager();
          const result = await _campaignManager.getSingleCampaignAsync(id);
          const localPcSettings = getLocalSettings(
            result.visualPcSettings,
            "pc",
          );
          const localPhoneSettings = getLocalSettings(
            result.visualPhoneSettings,
            "phone",
          );
          if (result.notificationVisualSettings?.roundness) {
            setSelectedBorder(result.notificationVisualSettings?.roundness);
            setSelectedBorderDraft(
              result.notificationVisualSettings?.roundness,
            );
          }
          setPcSettings(localPcSettings);
          setPcSettingsDraft(localPcSettings);
          setPhoneSettingsDraft(localPhoneSettings);
          setPhoneSettings(localPhoneSettings);
          setIsFetching(false);
        } catch (e: any) {
          setIsFetching(false);
          showFlashMessageWithTimeout(dispatch, e.toString(), "error");
        }
      };

      fetchCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaveChanges = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      await CampaignManager.getManager().updateCampaignAsync(
        id,
        campaignName,
        alignment === "all" ? {} : transformFormsToUrlFilterDto(forms),
        {
          textColor,
          backgroundColor,
          brandingIsShowingIcon: !!showIcon,
          borderRadius,
          backgroundOpacity,
          iconColor,
          iconBackgroundColor,
          iconBackgroundRadius,
          activeSize,
          logoDisplay: customWebBranding?.logoDisplay ?? true,
          logo: customWebBranding?.logo,
          logoBackgroundColor: customWebBranding?.logoBackgroundColor,
          logoText: customWebBranding?.logoText,
        },
      );

      await _campaignManager.updateCampaignVisualAsync(
        id,
        pcSettings,
        phoneSettings,
        { roundness: selectedBorder ?? 0 },
      );
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      showFlashMessageWithTimeout(dispatch, e.toString(), "error", 2000);
    }
  };

  useEffect(() => {
    if (!id || !campaignName || !isBrandingLoaded) return;

    onSaveChanges();
  }, [customWebBranding, isBrandingLoaded, campaignName]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const getBranding = async () => {
      setLoading(true);
      const result = await _campaignManager.getSingleCampaignAsync(id);
      const branding = result.branding;

      setShowIcon(!!branding?.brandingIsShowingIcon);
      setShowIconDraft(!!branding?.brandingIsShowingIcon);
      branding?.activeSize && setActiveSize(branding.activeSize);
      branding?.activeSize && setActiveSizeDraft(branding.activeSize);
      branding?.backgroundColor &&
        setBackgroundColor(branding?.backgroundColor);
      branding?.backgroundColor &&
        setBackgroundColorDraft(branding?.backgroundColor);
      branding?.backgroundOpacity &&
        setBackgroundOpacity(branding?.backgroundOpacity);
      branding?.backgroundOpacity &&
        setBackgroundOpacityDraft(branding?.backgroundOpacity);
      branding?.borderRadius && setBorderRadius(branding?.borderRadius);
      branding?.borderRadius && setBorderRadiusDraft(branding?.borderRadius);
      branding?.iconBackgroundColor &&
        setIconBackgroundColor(branding?.iconBackgroundColor);
      branding?.iconBackgroundColor &&
        setIconBackgroundColorDraft(branding?.iconBackgroundColor);
      branding?.iconBackgroundRadius &&
        setIconBackgroundRadius(branding?.iconBackgroundRadius);
      branding?.iconBackgroundRadius &&
        setIconBackgroundRadiusDraft(branding?.iconBackgroundRadius);
      branding?.iconColor && setIconColor(branding?.iconColor);
      branding?.iconColor && setIconColorDraft(branding?.iconColor);
      branding?.textColor && setTextColor(branding?.textColor);
      branding?.textColor && setTextColorDraft(branding?.textColor);

      setLoading(false);
      setIsBrandingLoaded(true);
    };

    getBranding();
  }, []);

  useEffect(() => {
    if (reset) {
      setCampaignName(campaignNameDraft);
      setAlignment(alignmentDraft);
      setForms(formsDraft);
      setPcSettings(pcSettingsDraft);
      setPhoneSettings(phoneSettingsDraft);
      setSelectedBorder(selectedBorderDraft);
      setShowIcon(true);
      setTextColor(textColorDraft);
      setBackgroundColor(backgroundColorDraft);
      setBorderRadius(borderRadiusDraft);
      setBackgroundOpacity(backgroundOpacityDraft);
      setIconColor(iconColorDraft);
      setIconBackgroundColor(iconBackgroundColorDraft);
      setIconBackgroundRadius(iconBackgroundRadiusDraft);
      setActiveSize(activeSizeDraft);
      setShowIcon(showIconDraft);
      setReset(false);
      setDetectedChange(false);
    }
  }, [reset]);

  useEffect(() => {
    if (save) {
      onSaveChanges();
      setSave(false);
      setDetectedChange(false);
    }
  }, [save]);

  const value = {
    alignment,
    forms,
    pcPreview,
    phonePreview,
    menuItems: [
      { title: "Začíná na", value: "beginsWith" },
      { title: "Obsahuje", value: "contains" },
      { title: "Shoduje se", value: "isEqual" },
    ],
    campaignName,
    selectedTab,
    selectedBorder,
    editCampaignName,
    isFetching,
    isLoading,
    isDefaultUpdateLoading,
    isModalOpen,
    pcSettings,
    phoneSettings,
    setPcSettings,
    setPhoneSettings,
    createPcPreview,
    createPhonePreview,
    setIsDefaultUpdateLoading,
    setIsModalOpen,
    setPcPreview,
    setPhonePreview,
    handleChange: (event: SyntheticEvent, newValue: string) => {
      setSelectedTab(newValue as TabType);
      sessionStorage.setItem("selectedTab", newValue);
    },
    handleEditCampaignName: () => setEditCampaignName(true),
    handleFormControlChange: (event: SelectChangeEvent, index: number) => {
      setForms(state => {
        const copy: FormType[] = [...state];
        const formToUpdate = copy[index];
        formToUpdate.control = event.target.value as ControlType;
        return copy;
      });
      setDetectedChange(true);
    },
    handleAlignmentChange: (event: any, newAlignment: "all" | "custom") => {
      if (newAlignment) {
        setAlignment(newAlignment);
        setDetectedChange(true);
      }
    },
    handleNameChange: (event: ChangeEvent<HTMLInputElement>) => {
      setCampaignName(event.target.value);
    },
    getValuesToHide: (forms: FormType[]) => {
      let unavailableControls: string[] = [];
      forms.forEach(form => unavailableControls.push(form.control));
      return unavailableControls;
    },
    getLocalSettings,
    onDeleteForm: (index: number) => {
      setForms(state => {
        const copy: FormType[] = [...state];
        copy.splice(index, 1);
        return copy;
      });
      setDetectedChange(true);
    },
    onAddFormIncludeChip: (formIndex: number, values: string[]) => {
      setForms(state => {
        const copy: FormType[] = [...state];
        copy[formIndex].include = values;
        return copy;
      });
      setDetectedChange(true);
    },
    onAddExcludeChip: (formIndex: number, values: string[]) => {
      setForms(state => {
        const copy: FormType[] = [...state];
        copy[formIndex].excludes = values;
        return copy;
      });
      setDetectedChange(true);
    },
    onSaveChanges,
    onCampaignNameChanges: (newName: string) => {
      setCampaignName(newName);
      setEditCampaignName(false);
    },
    onCampaignNameSave: async (newName: string) => {
      if (!id) {
        return;
      }

      try {
        await _campaignManager.updateCampaignAsync(
          id,
          newName,
          alignment === "all" ? {} : transformFormsToUrlFilterDto(forms),
        );
        setCampaignName(newName);
        setEditCampaignName(false);
      } catch (e: any) {
        showFlashMessageWithTimeout(dispatch, e.toString(), "error", 2000);
      }
    },
    addEmptyForm: () => {
      if (forms.length < 3) {
        setForms(state => [
          ...state,
          { control: "contains", include: [], excludes: [] },
        ]);
      }
    },
    showIcon,
    textColor,
    backgroundColor,
    borderRadius,
    backgroundOpacity,
    iconColor,
    iconBackgroundColor,
    iconBackgroundRadius,
    modalOpen,
    activeSize,
    setActiveSize,
    setTextColor,
    setBackgroundColor,
    setBorderRadius,
    setBackgroundOpacity,
    setShowIcon,
    setIconBackgroundColor,
    setIconBackgroundRadius,
    setIconColor,
    setModalOpen,
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export const useCampaign = () => {
  const context = useContext(CampaignContext);

  if (!context) {
    throw new Error("error");
  }

  return context;
};
